import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { whatsappService } from '../services/whatsapp';
import { MessageCircle, X, Phone, User } from 'lucide-react';
import { ButtonLoader } from './LoadingStates';

interface WhatsAppShareModalProps {
    /** Order to share; null hides the modal. */
    orderId: string | null;
    /** Prefill values (e.g. from the settle form). */
    defaultMobile?: string;
    defaultName?: string;
    onClose: () => void;
}

/**
 * Post-settle "Share bill on WhatsApp?" dialog. The number is prefilled when
 * known; otherwise the user enters it — the backend reuses the matching
 * customer or saves a new one (name + mobile) before sending.
 */
export const WhatsAppShareModal: React.FC<WhatsAppShareModalProps> = ({
    orderId,
    defaultMobile,
    defaultName,
    onClose,
}) => {
    const { currentTheme } = useTheme();
    const [mobile, setMobile] = useState('');
    const [name, setName] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (orderId) {
            setMobile(defaultMobile || '');
            setName(defaultName || '');
        }
    }, [orderId, defaultMobile, defaultName]);

    if (!orderId) return null;

    const handleSend = async () => {
        const clean = mobile.replace(/\D/g, '');
        if (!/^[6-9]\d{9}$/.test(clean)) {
            toast.error('Enter a valid 10-digit mobile number');
            return;
        }
        try {
            setSending(true);
            const res = await whatsappService.shareBill(orderId, { mobile: clean, name: name.trim() || undefined });
            if (res.status === 'SENT') {
                toast.success(`Bill sent on WhatsApp to ${res.mobile}`);
                onClose();
            } else {
                toast.error(res.message || 'Failed to send WhatsApp bill');
            }
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send WhatsApp bill');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-3">
            <div
                className="max-w-md w-full rounded-xl border p-5"
                style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}
            >
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#25D36622' }}>
                            <MessageCircle className="h-5 w-5" style={{ color: '#25D366' }} />
                        </div>
                        <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                            Share Bill on WhatsApp?
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-lg transition-colors"
                        style={{ backgroundColor: currentTheme.colors.background.secondary }}
                    >
                        <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                    </button>
                </div>
                <p className="text-sm mb-4" style={{ color: currentTheme.colors.text.secondary }}>
                    Order settled. Send the bill PDF to the customer on WhatsApp?
                </p>

                <div className="space-y-3 mb-5">
                    <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                        <input
                            type="tel"
                            value={mobile}
                            onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            placeholder="WhatsApp number (10 digits)"
                            className="block w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary, color: currentTheme.colors.text.primary }}
                        />
                    </div>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Customer name (saved if new customer)"
                            className="block w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary, color: currentTheme.colors.text.primary }}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        disabled={sending}
                        className="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-50"
                        style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.secondary, backgroundColor: currentTheme.colors.background.secondary }}
                    >
                        Skip
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending}
                        className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                        style={{ backgroundColor: '#25D366' }}
                    >
                        {sending ? <ButtonLoader text="Sending..." /> : (<><MessageCircle className="h-4 w-4" /> Send Bill</>)}
                    </button>
                </div>
            </div>
        </div>
    );
};
